import { SelectionModel } from '@angular/cdk/collections';
import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    AtokuCreatePricingProfileRequest,
    AtokuPricingProfile,
    AtokuPricingProfileItemPriceRequest,
    AtokuService,
    AtokuUpsertPricingProfilePricesRequest
} from '../services/atoku.service';
import {
    CreatePricingProfileDialogComponent,
    CreatePricingProfileDialogResult
} from './dialogs/create-pricing-profile-dialog/create-pricing-profile-dialog.component';
import {
    EditPricingProfileDialogComponent,
    EditPricingProfileDialogResult
} from './dialogs/edit-pricing-profile-dialog/edit-pricing-profile-dialog.component';

type AtokuPricingTab = 'stations' | 'storage' | 'equipment';

interface PricingItem {
    id: string;
    name: string;
    defaultPrice: number;
}

interface PricingRowView {
    id: string;
    code: string;
    name: string;
    currentPrice: number;
    comparePrice?: number;
    variance?: number;
}

@Component({
    selector: 'app-atoku-pricing-profiles',
    templateUrl: './atoku-pricing-profiles.component.html',
    styleUrls: ['./atoku-pricing-profiles.component.scss']
})
export class AtokuPricingProfilesComponent {
    profileColumns: string[] = ['select', 'code', 'name', 'description'];
    selectedTab: AtokuPricingTab = 'stations';
    activeProfileCode = '';
    private _compareProfileCode: string | null = null;
    profileFilterValue = '';

    profiles: AtokuPricingProfile[] = [];
    itemRows: PricingRowView[] = [];
    selection = new SelectionModel<AtokuPricingProfile>(true, []);

    private itemsByTab: Record<AtokuPricingTab, PricingItem[]> = {
        stations: [],
        storage: [],
        equipment: []
    };
    private pricingMatrix: Record<string, Record<AtokuPricingTab, Record<string, number>>> = {};
    private dirtyKeys = new Set<string>();
    isSaving = false;

    constructor(
        private dialog: MatDialog,
        private atokuService: AtokuService
    ) {
        this.loadData();
    }

    get compareProfileCode(): string | null {
        return this._compareProfileCode;
    }

    set compareProfileCode(value: string | null) {
        this._compareProfileCode = value;
        this.rebuildItemRows();
    }

    get filteredProfiles(): AtokuPricingProfile[] {
        const filter = this.profileFilterValue.trim().toLowerCase();
        if (!filter) {
            return this.profiles;
        }

        return this.profiles.filter(profile =>
            [profile.code, profile.name, profile.description]
                .join(' ')
                .toLowerCase()
                .includes(filter)
        );
    }

    get activeProfile(): AtokuPricingProfile {
        return this.profiles.find(profile => profile.code === this.activeProfileCode) ?? {
            id: 0,
            code: '',
            name: '',
            description: ''
        };
    }

    get compareProfiles(): AtokuPricingProfile[] {
        return this.profiles.filter(profile => profile.code !== this.activeProfileCode);
    }

    get hasSelection(): boolean {
        return this.selection.selected.length > 0;
    }

    get hasSingleSelection(): boolean {
        return this.selection.selected.length === 1;
    }

    get itemPriceColumns(): string[] {
        return this.compareProfileCode
            ? ['code', 'name', 'currentPrice', 'comparePrice', 'variance']
            : ['code', 'name', 'currentPrice'];
    }

    get hasUnsavedChanges(): boolean {
        if (!this.activeProfileCode) {
            return false;
        }
        const keyPrefix = `${this.activeProfileCode}|`;
        for (const key of this.dirtyKeys) {
            if (key.startsWith(keyPrefix)) {
                return true;
            }
        }
        return false;
    }

    selectProfile(profile: AtokuPricingProfile): void {
        this.activeProfileCode = profile.code;
        if (this.compareProfileCode === profile.code) {
            this.compareProfileCode = null;
            return;
        }
        this.rebuildItemRows();
    }

    applyProfileFilter(event: Event): void {
        this.profileFilterValue = (event.target as HTMLInputElement).value ?? '';
    }

    addProfile(): void {
        const dialogRef = this.dialog.open(CreatePricingProfileDialogComponent, {
            width: '640px',
            data: {
                existingProfiles: this.profiles.map(profile => ({ code: profile.code, name: profile.name }))
            }
        });

        dialogRef.afterClosed().subscribe((result?: CreatePricingProfileDialogResult) => {
            if (!result || this.profiles.some(profile => profile.code === result.code)) {
                return;
            }

            const createRequest: AtokuCreatePricingProfileRequest = {
                Code: result.code,
                Name: result.name,
                Description: result.description
            };

            this.atokuService.createPricingProfile(createRequest).subscribe(created => {
                const createdProfile: AtokuPricingProfile = {
                    id: Number((created as Record<string, unknown>)['id'] ?? Date.now()),
                    code: String((created as Record<string, unknown>)['code'] ?? result.code),
                    name: String((created as Record<string, unknown>)['name'] ?? result.name),
                    description: String((created as Record<string, unknown>)['description'] ?? result.description)
                };

                this.profiles = [...this.profiles, createdProfile];
                this.pricingMatrix[createdProfile.code] = result.copyFromProfileCode
                    ? {
                        stations: this.clonePrices(result.copyFromProfileCode, 'stations'),
                        storage: this.clonePrices(result.copyFromProfileCode, 'storage'),
                        equipment: this.clonePrices(result.copyFromProfileCode, 'equipment')
                    }
                    : {
                        stations: this.createDefaultPriceMap('stations'),
                        storage: this.createDefaultPriceMap('storage'),
                        equipment: this.createDefaultPriceMap('equipment')
                    };

                this.activeProfileCode = createdProfile.code;
                this.rebuildItemRows();

                if (result.copyFromProfileCode) {
                    this.markProfileDirty(createdProfile.code);
                    this.savePrices();
                }
            });
        });
    }

    editSelectedProfile(): void {
        const profile = this.selection.selected[0];
        if (!profile.code) {
            return;
        }

        const dialogRef = this.dialog.open(EditPricingProfileDialogComponent, {
            width: '560px',
            data: {
                code: profile.code,
                name: profile.name,
                description: profile.description
            }
        });

        dialogRef.afterClosed().subscribe((result?: EditPricingProfileDialogResult) => {
            if (!result) {
                return;
            }

            this.profiles = this.profiles.map(item => item.code === profile.code
                ? { ...item, name: result.name, description: result.description }
                : item);
        });
    }

    removeSelectedProfiles(): void {
        const selectedCodes = new Set(this.selection.selected.map(profile => profile.code));
        if (!selectedCodes.size) {
            return;
        }

        this.profiles = this.profiles.filter(profile => !selectedCodes.has(profile.code));
        selectedCodes.forEach(code => {
            delete this.pricingMatrix[code];
            this.clearDirtyForProfile(code);
        });

        if (this.compareProfileCode && selectedCodes.has(this.compareProfileCode)) {
            this.compareProfileCode = null;
        }

        if (!this.profiles.some(profile => profile.code === this.activeProfileCode)) {
            this.activeProfileCode = this.profiles[0]?.code ?? '';
        }

        this.selection.clear();
        this.rebuildItemRows();
    }

    toggleProfileSelection(profile: AtokuPricingProfile): void {
        this.selection.toggle(profile);
    }

    isAllProfilesSelected(): boolean {
        const rows = this.filteredProfiles;
        return rows.length > 0 && rows.every(row => this.selection.isSelected(row));
    }

    masterToggleProfiles(): void {
        const rows = this.filteredProfiles;
        if (!rows.length) {
            return;
        }

        if (this.isAllProfilesSelected()) {
            rows.forEach(row => this.selection.deselect(row));
            return;
        }

        rows.forEach(row => this.selection.select(row));
    }

    savePrices(): void {
        if (!this.activeProfileCode || this.isSaving || !this.hasUnsavedChanges) {
            return;
        }

        const payload: AtokuUpsertPricingProfilePricesRequest = {
            PricingProfileCode: this.activeProfileCode,
            Storage: this.toItemPriceRequests('storage'),
            Equipment: this.toItemPriceRequests('equipment'),
            SubSpaces: this.toItemPriceRequests('stations')
        };

        this.isSaving = true;
        this.atokuService.upsertPricingProfilePrices(payload).subscribe({
            next: () => {
                this.clearDirtyForProfile(this.activeProfileCode);
                this.isSaving = false;
            },
            error: () => {
                this.isSaving = false;
            }
        });
    }

    setTab(index: number): void {
        this.selectedTab = (['stations', 'storage', 'equipment'] as AtokuPricingTab[])[index] ?? 'stations';
        this.rebuildItemRows();
    }

    updatePrice(row: PricingRowView, value: string | number): void {
        const parsed = Number(value);
        const safeValue = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
        const profilePrices = this.pricingMatrix[this.activeProfileCode]?.[this.selectedTab];
        if (!profilePrices) {
            return;
        }

        const nextValue = Math.round(safeValue * 100) / 100;
        profilePrices[row.id] = nextValue;
        row.currentPrice = nextValue;
        row.variance = row.comparePrice === undefined ? undefined : nextValue - row.comparePrice;
        this.dirtyKeys.add(this.getDirtyKey(this.activeProfileCode, this.selectedTab, row.id));
    }

    private rebuildItemRows(): void {
        const items = this.itemsByTab[this.selectedTab] ?? [];
        this.itemRows = items.map((item, index) => {
            const currentPrice = this.pricingMatrix[this.activeProfileCode]?.[this.selectedTab]?.[item.id] ?? item.defaultPrice;
            const comparePrice = this.compareProfileCode
                ? this.pricingMatrix[this.compareProfileCode]?.[this.selectedTab]?.[item.id] ?? item.defaultPrice
                : undefined;

            return {
                id: item.id,
                code: this.getAtokuPricingItemCode(this.selectedTab, index + 1),
                name: item.name,
                currentPrice,
                comparePrice,
                variance: comparePrice === undefined ? undefined : currentPrice - comparePrice
            };
        });
    }

    private loadData(): void {
        this.atokuService.getStations().subscribe(stations => {
            this.itemsByTab.stations = stations.map(station => {
                const raw = station as unknown as Record<string, unknown>;
                return {
                    id: String(raw['id'] ?? ''),
                    name: String(raw['name'] ?? ''),
                    defaultPrice: Number(raw['price'] ?? 0)
                };
            }).filter(item => item.id);
            this.ensureAllProfileMapsInitialized();
            this.rebuildItemRows();
        });

        this.atokuService.getEquipment('storage').subscribe(items => {
            this.itemsByTab.storage = items.map(item => this.toPricingItem(item)).filter(itemPrice => itemPrice.id);
            this.ensureAllProfileMapsInitialized();
            this.rebuildItemRows();
        });

        this.atokuService.getEquipment('equipment').subscribe(items => {
            this.itemsByTab.equipment = items.map(item => this.toPricingItem(item)).filter(itemPrice => itemPrice.id);
            this.ensureAllProfileMapsInitialized();
            this.rebuildItemRows();
        });

        this.atokuService.getPricingProfiles().subscribe(profiles => {
            this.profiles = profiles;
            this.activeProfileCode = this.activeProfileCode || profiles[0]?.code || '';
            this.selection.clear();

            profiles.forEach(profile => {
                this.pricingMatrix[profile.code] = {
                    stations: this.toPriceMap(profile['subSpaces'] ?? profile['stations']),
                    storage: this.toPriceMap(profile['storage']),
                    equipment: this.toPriceMap(profile['equipment'])
                };
            });

            this.ensureAllProfileMapsInitialized();
            this.rebuildItemRows();
        });
    }

    private toPricingItem(item: unknown): PricingItem {
        const raw = item as Record<string, unknown>;
        return {
            id: String(raw['id'] ?? ''),
            name: String(raw['equipmentName'] ?? raw['name'] ?? ''),
            defaultPrice: Number(raw['price'] ?? 0)
        };
    }

    private toPriceMap(value: unknown): Record<string, number> {
        if (!value || typeof value !== 'object') {
            return {};
        }
        return Object.entries(value as Record<string, unknown>).reduce<Record<string, number>>((acc, [key, price]) => {
            acc[String(key)] = Number(price ?? 0);
            return acc;
        }, {});
    }

    private createDefaultPriceMap(tab: AtokuPricingTab): Record<string, number> {
        return (this.itemsByTab[tab] ?? []).reduce<Record<string, number>>((acc, item) => {
            acc[item.id] = item.defaultPrice;
            return acc;
        }, {});
    }

    private clonePrices(sourceProfileCode: string, tab: AtokuPricingTab): Record<string, number> {
        return { ...(this.pricingMatrix[sourceProfileCode]?.[tab] ?? {}) };
    }

    private ensureAllProfileMapsInitialized(): void {
        this.profiles.forEach(profile => this.ensureProfileMap(profile.code));
    }

    private ensureProfileMap(profileCode: string): void {
        if (!this.pricingMatrix[profileCode]) {
            this.pricingMatrix[profileCode] = {
                stations: {},
                storage: {},
                equipment: {}
            };
        }

        (['stations', 'storage', 'equipment'] as AtokuPricingTab[]).forEach(tab => {
            const map = this.pricingMatrix[profileCode][tab] ?? {};
            this.itemsByTab[tab].forEach(item => {
                if (map[item.id] === undefined) {
                    map[item.id] = item.defaultPrice;
                }
            });
            this.pricingMatrix[profileCode][tab] = map;
        });
    }

    private toItemPriceRequests(tab: AtokuPricingTab): AtokuPricingProfileItemPriceRequest[] {
        const prices = this.pricingMatrix[this.activeProfileCode]?.[tab] ?? {};
        return this.itemsByTab[tab].map(item => ({
            id: item.id,
            price: Number(prices[item.id] ?? 0),
            currencyCode: 'ZAR',
            unit: 1
        }));
    }

    private clearDirtyForProfile(profileCode: string): void {
        const prefix = `${profileCode}|`;
        this.dirtyKeys.forEach(key => {
            if (key.startsWith(prefix)) {
                this.dirtyKeys.delete(key);
            }
        });
    }

    private markProfileDirty(profileCode: string): void {
        (['stations', 'storage', 'equipment'] as AtokuPricingTab[]).forEach(tab => {
            this.itemsByTab[tab].forEach(item => {
                this.dirtyKeys.add(this.getDirtyKey(profileCode, tab, item.id));
            });
        });
    }

    private getDirtyKey(profileCode: string, tab: AtokuPricingTab, itemId: string): string {
        return `${profileCode}|${tab}|${itemId}`;
    }

    private getAtokuPricingItemCode(tab: AtokuPricingTab, index: number): string {
        const prefix = tab === 'stations' ? 'ST' : tab === 'storage' ? 'STR' : 'EQ';
        return `${prefix}-${String(index).padStart(3, '0')}`;
    }
}
